import {
  MdAddCircleOutline,
  MdArrowDropDown,
  MdOutlineArrowCircleLeft,
} from "react-icons/md";
import InputFile from "components/main-view/utils/placeholders/InputFile";
import { useState } from "react";
import {
  Course,
  Program,
  Project,
  Module,
  InstructionStep,
} from "utils/interfaces";
import { AnimatePresence } from "framer-motion";
import OptionsPanel from "components/main-view/utils/pop-up-panels/options-pop-up";
import { lessonCategoriesList } from "config/optionValues";
import { usePlatform } from "components/utils/PlatformContext";
import CreateEditStepByStepInstructions from "../pop-up-panels/create-edit-step-by-step-instructions";
import { fetchPdf } from "services/filesRequests";
import base64toBlob from "utils/base64toBlob";
import FileCard from "../placeholders/FileCard";
import InstructionsCard from "../placeholders/InstructionsCard";
import AddInstructionsPopUp from "./add-instructions-pop-up";

interface AdditionalAttachmentsProps {
  selectedLesson: string;
  selectedData: Program | Course | Project | Module | null;
  activateSaveButton: boolean;
  videoUrl: string;
  codingLanguage: string;
  planDocument: string | null;
  planName: string;
  planSize: number;
  initialCodeName: string;
  initialCodeSize: number;
  finalCodeName: string;
  finalCodeSize: number;
  stepByStepInstructions: InstructionStep[];
  setVideoUrl: React.Dispatch<React.SetStateAction<string>>;
  setCodingLanguage: React.Dispatch<React.SetStateAction<string>>;
  setPlanDocument: React.Dispatch<React.SetStateAction<string | null>>;
  setPlanName: React.Dispatch<React.SetStateAction<string>>;
  setPlanSize: React.Dispatch<React.SetStateAction<number>>;
  setStage: React.Dispatch<React.SetStateAction<number>>;
  setInitialCode: React.Dispatch<React.SetStateAction<string | null>>;
  setFinalCode: React.Dispatch<React.SetStateAction<string | null>>;
  setInitialCodeName: React.Dispatch<React.SetStateAction<string>>;
  setInitialCodeSize: React.Dispatch<React.SetStateAction<number>>;
  setFinalCodeName: React.Dispatch<React.SetStateAction<string>>;
  setFinalCodeSize: React.Dispatch<React.SetStateAction<number>>;
  setStepByStepInstructions: React.Dispatch<
    React.SetStateAction<InstructionStep[]>
  >;
  onSave: () => void;
}

const AdditionalAttachments: React.FC<AdditionalAttachmentsProps> = ({
  selectedLesson,
  selectedData,
  activateSaveButton,
  videoUrl,
  codingLanguage,
  planDocument,
  planName,
  planSize,
  initialCodeName,
  initialCodeSize,
  finalCodeName,
  finalCodeSize,
  stepByStepInstructions,
  setVideoUrl,
  setCodingLanguage,
  setPlanDocument,
  setPlanName,
  setPlanSize,
  setStage,
  setInitialCode,
  setFinalCode,
  setInitialCodeName,
  setInitialCodeSize,
  setFinalCodeName,
  setFinalCodeSize,
  onSave,
  setStepByStepInstructions,
}) => {
  // States definition
  const [showCodingLanguagePopUp, setShowCodingLanguagePopUp] = useState(false);
  const [isAddInstructionsPopUpOpen, setIsAddInstructionsPopUpOpen] =
    useState(false);
  const [showSBSInstructionsPopUp, setShowSBSInstructionsPopUp] =
    useState(false);

  // Determine set of available "programming language" options.
  const { lessonCategories } = usePlatform();
  const availableOptions = lessonCategories.map(
    (value: string) =>
      lessonCategoriesList.find((category) => category.value === value)?.text ??
      value,
  );

  /**
   * Go to the previous stage
   */
  const handlePrev = () => {
    setStage(2);
  };

  /**
   * Function executed when click on the "Add Instructions" button
   */
  const handleUploadInstructions = () => {
    setIsAddInstructionsPopUpOpen(true);
  };

  /**
   * Function to apply on click name
   */
  const onClickNameFunction = (fileInput: string | null) => {
    if (fileInput) {
      const blob = base64toBlob(fileInput);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
    } else if (!!selectedData) {
      // Get the pdf
      fetchPdf(selectedLesson, selectedData._id).then((url: string) => {
        // And open the pdf in a new window
        window.open(url);
      });
    }
  };

  /**
   * Function used when pressing the delete button on FileCard
   */
  const removeFile = (
    setFile: React.Dispatch<React.SetStateAction<string | null>>,
    setFilename: React.Dispatch<React.SetStateAction<string>>,
    setFilesize: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setFile("");
    setFilename("");
    setFilesize(0);
  };

  /**
   * Function executed when clicking the edit button on the instructions card
   */
  const handleClickEditInstructions = () => {
    setShowSBSInstructionsPopUp(true);
  };

  const handleClickDeleteInstructions = () => {
    // Delete the current instructions
    setStepByStepInstructions([]);
  };

  return (
    <div className="w-[562px] h-fit p-6 bg-white rounded-3xl border-neutral-200 justify-start items-start gap-2 inline-flex">
      <div className="w-full flex-col justify-start items-start gap-12 inline-flex">
        <div className="self-stretch h-7 text-neutral-700 text-[23px] font-semibold font-sans leading-7">
          Additional attachments
        </div>
        <div className="self-stretch h-fit flex-col justify-start items-start gap-12 flex">
          <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
            {/* Coding language */}
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
                Lesson Category{selectedLesson === "projects" ? "" : "*"}
              </div>
              <button
                className="self-stretch p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-lg justify-start items-center text-start gap-2 inline-flex cursor-pointer"
                onClick={() => setShowCodingLanguagePopUp(true)}
                title={`Pick a language for this ${selectedLesson.slice(
                  0,
                  -1,
                )}`}
              >
                <div className="grow shrink basis-0 h-[27px] py-1 justify-start items-start gap-2 flex">
                  <div
                    className={`grow shrink basis-0 h-[19px] ${
                      codingLanguage ? "text-neutral-700" : "text-neutral-300"
                    } text-base font-normal font-sans leading-[19px]`}
                  >
                    {codingLanguage
                      ? codingLanguage
                      : `Pick a language for this ${selectedLesson.slice(
                          0,
                          -1,
                        )}`}
                  </div>
                </div>
                <MdArrowDropDown className="w-5 h-5 fill-neutral-600" />
              </button>
            </div>

            {/* Add instructions environment */}
            {codingLanguage === "Scratch" ? (
              // Show the step by step instructions with more precedence than PDF files
              stepByStepInstructions && stepByStepInstructions.length > 0 ? (
                <InstructionsCard
                  stepsNumber={stepByStepInstructions.length}
                  onEditClick={handleClickEditInstructions}
                  onDeleteClick={handleClickDeleteInstructions}
                />
              ) : (
                <AnimatePresence initial={false} mode="wait">
                  {planName ? (
                    <FileCard
                      key="file-card"
                      filename={planName}
                      filesize={planSize}
                      onDeleteClick={() =>
                        removeFile(setPlanDocument, setPlanName, setPlanSize)
                      }
                      onClickNameFunction={() =>
                        onClickNameFunction(planDocument)
                      }
                      initialAnimation={{ opacity: 0, y: "50%" }}
                    />
                  ) : (
                    // Add instructions button
                    <div
                      key="add-instructions-button"
                      className="group inline-flex justify-left items-center gap-2 cursor-pointer"
                      onClick={handleUploadInstructions}
                    >
                      <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                      <div className="justify-start text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-5">
                        Add Instructions
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              )
            ) : !!codingLanguage ? (
              <AnimatePresence initial={false}>
                {planName ? (
                  <FileCard
                    key="file-card"
                    filename={planName}
                    filesize={planSize}
                    onDeleteClick={() =>
                      removeFile(setPlanDocument, setPlanName, setPlanSize)
                    }
                    onClickNameFunction={() =>
                      onClickNameFunction(planDocument)
                    }
                    initialAnimation={{ opacity: 0, y: "50%" }}
                  />
                ) : (
                  // Plan document
                  <InputFile
                    key="input-file"
                    title={`Upload a ${selectedLesson.slice(0, -1)}`}
                    setFile={setPlanDocument}
                    setFilename={setPlanName}
                    setFilesize={setPlanSize}
                    keyName="plan-document"
                  />
                )}
              </AnimatePresence>
            ) : (
              <></>
            )}

            {/* Video URL */}
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <div className="rounded justify-start items-start gap-2 inline-flex">
                <label
                  htmlFor="video-url"
                  className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide"
                >
                  Video URL{selectedLesson === "projects" ? "" : "*"}
                </label>
              </div>
              <div className="w-full h-[51px] bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                <input
                  id="video-url"
                  type="text"
                  value={videoUrl}
                  onChange={(event) => setVideoUrl(event.target.value)}
                  className={`w-full h-full p-3 ${
                    videoUrl ? "text-neutral-700" : "text-neutral-300"
                  } text-base font-normal font-sans leading-[19px] bg-transparent border-none focus:outline-none`}
                  placeholder={`Add URL for the video`}
                />
              </div>
            </div>

            {/* Initial file */}
            {codingLanguage &&
              ![
                "Minecraft",
                "Micro:bit",
                "Cultural Capabilities",
                "Video and PDF",
              ].includes(codingLanguage) && (
                <div className="w-full self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
                  <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
                    Initial code{selectedLesson === "projects" ? "" : "*"}
                  </div>
                  {initialCodeName ? (
                    <FileCard
                      filename={initialCodeName}
                      filesize={initialCodeSize}
                      onDeleteClick={() =>
                        removeFile(
                          setInitialCode,
                          setInitialCodeName,
                          setInitialCodeSize,
                        )
                      }
                    />
                  ) : (
                    <InputFile
                      title={`Upload a ${selectedLesson.slice(
                        0,
                        -1,
                      )} Initial code`}
                      accept={
                        codingLanguage === "Scratch"
                          ? ".sb3"
                          : codingLanguage === "Python"
                            ? ".py"
                            : codingLanguage === "JavaScript"
                              ? ".js"
                              : ""
                      }
                      setFile={setInitialCode}
                      setFilename={setInitialCodeName}
                      setFilesize={setInitialCodeSize}
                      keyName="initial-code"
                    />
                  )}
                </div>
              )}

            {/* Final file */}
            {codingLanguage &&
              !["Cultural Capabilities", "Video and PDF"].includes(
                codingLanguage,
              ) && (
                <div className="w-full self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
                  <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
                    Final code{selectedLesson === "projects" ? "" : "*"}
                  </div>
                  {finalCodeName ? (
                    <FileCard
                      filename={finalCodeName}
                      filesize={finalCodeSize}
                      onDeleteClick={() =>
                        removeFile(
                          setFinalCode,
                          setFinalCodeName,
                          setFinalCodeSize,
                        )
                      }
                    />
                  ) : (
                    <InputFile
                      title={`Upload a ${selectedLesson.slice(
                        0,
                        -1,
                      )} Final code`}
                      accept={
                        codingLanguage === "Scratch"
                          ? ".sb3"
                          : codingLanguage === "Python"
                            ? ".py"
                            : codingLanguage === "JavaScript"
                              ? ".js"
                              : codingLanguage === "Minecraft"
                                ? ".py"
                                : codingLanguage === "Micro:bit"
                                  ? ".hex"
                                  : ""
                      }
                      setFile={setFinalCode}
                      setFilename={setFinalCodeName}
                      setFilesize={setFinalCodeSize}
                      keyName="final-code"
                    />
                  )}
                </div>
              )}
          </div>
        </div>
        <div className="self-stretch h-[46px] justify-between items-center inline-flex">
          <button
            className="group w-fit h-fit rounded justify-center items-center gap-2 flex cursor-pointer"
            onClick={handlePrev}
          >
            <MdOutlineArrowCircleLeft className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
            <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug select-none">
              Prev
            </div>
          </button>
          {activateSaveButton ? (
            <button
              className="w-[219px] h-[46px] px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 flex rounded justify-center items-center gap-2 cursor-pointer"
              onClick={onSave}
            >
              <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                Save
              </div>
            </button>
          ) : (
            <div className="w-[219px] h-[46px] px-5 py-3 bg-Subtle flex rounded justify-center items-center gap-2 cursor-not-allowed">
              <div className="text-neutral-400 text-lg font-semibold font-sans leading-snug select-none">
                Save
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Pop up panels */}
      <AnimatePresence>
        {/* Coding language pop-up */}
        {showCodingLanguagePopUp && (
          <OptionsPanel
            options={availableOptions}
            setShowPopUp={setShowCodingLanguagePopUp}
            setOptions={setCodingLanguage}
          />
        )}
        {/* Show the instruction's options panel */}
        {isAddInstructionsPopUpOpen && (
          <AddInstructionsPopUp
            setShowSBSInstructionsPopUp={setShowSBSInstructionsPopUp}
            onClose={() => setIsAddInstructionsPopUpOpen(false)}
          />
        )}
        {/* Step by step instructions create/edit pop up */}
        {showSBSInstructionsPopUp && (
          <CreateEditStepByStepInstructions
            instructionSteps={stepByStepInstructions}
            setInstructionSteps={setStepByStepInstructions}
            handleClosePopUp={() => setShowSBSInstructionsPopUp(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdditionalAttachments;
